// frontend/src/pages/manager/tenant/TenantView.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ManagerSidebar from '../../../components/Sidebar/ManagerSidebar';

const TenantView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const res = await axios.get(
          `https://ikaze-thouse-backend.vercel.app/api/v1/tenants/get/${id}`
        );

        if (res.data.success) {
          setTenant(res.data.data);
        } else {
          console.error('Failed to fetch tenant:', res.data.message);
        }
      } catch (err) {
        console.error('Error fetching tenant details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [id]);

  // ================= HANDLE PASSPORT URL =================
  const getPassportUrl = () => {
    if (!tenant?.passport_file) return null;

    // If already full URL (Supabase)
    if (tenant.passport_file.startsWith('http')) {
      return tenant.passport_file;
    }

    // If relative path (backend)
    return `https://ikaze-thouse-backend.vercel.app/${tenant.passport_file}`;
  };

  const passportUrl = getPassportUrl();

  // ================= STYLES =================
  const styles = {
    layout: { display: 'flex', minHeight: '100vh', backgroundColor: '#f0f2f5' },
    sidebar: { width: '250px' },
    box: {
      flex: 1,
      background: '#fff',
      borderLeft: '4px solid #007bff',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      maxWidth: '1200px',
      paddingTop: '150px',
      margin: '20px auto',
      padding: '24px',
    },
    heading: { textAlign: 'center', color: '#007bff', marginBottom: '24px' },
    detailsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '20px',
      justifyContent: 'space-between',
    },
    detailBox: {
      flex: '1 1 calc(33.333% - 20px)',
      minWidth: '250px',
      maxWidth: 'calc(33.333% - 20px)',
      background: '#f9f9f9',
      padding: '16px',
      borderRadius: '8px',
      boxSizing: 'border-box',
    },
    label: {
      fontWeight: 'bold',
      color: '#333',
      display: 'block',
      marginBottom: '6px',
    },
    value: { color: '#007bff', fontSize: '16px' },
    closeBtn: {
      marginTop: '30px',
      padding: '10px 20px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'block',
      marginLeft: 'auto',
      fontSize: '16px',
    },
    passportLink: {
      marginTop: '10px',
      padding: '8px 16px',
      backgroundColor: '#28a745',
      color: 'white',
      borderRadius: '6px',
      textDecoration: 'none',
      display: 'inline-block',
      textAlign: 'center',
      fontSize: '14px',
    },
    noDoc: {
      color: 'red',
      fontSize: '14px',
      marginTop: '10px',
    },
    iframe: {
      width: '100%',
      height: '500px',
      marginTop: '20px',
      borderRadius: '10px',
      border: '1px solid #ddd',
    },
  };

  // ================= LOADING =================
  if (loading) {
    return (
      <p style={{ textAlign: 'center', marginTop: '50px' }}>
        Loading tenant details...
      </p>
    );
  }

  if (!tenant) {
    return (
      <p style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>
        Tenant not found.
      </p>
    );
  }

  // ================= DATA =================
  const tenantFields = [
    ['🏠 Name', tenant.guest_name],
    ['📞 Phone', tenant.mobile_number],
    ['📧 Email', tenant.email_address],
    ['🏘️ Apartment Unit', tenant.appartment_unite],
    ['🛬 Arrival Date', tenant.arrival_date?.split('T')[0]],
    ['🚪 Departure Date', tenant.departure_date?.split('T')[0]],
    ['💳 Rent', tenant.rent ? `${tenant.rent} USD` : 'N/A'],
    ['💳 Deposites', tenant.deposites ? `${tenant.deposites} USD` : 'N/A'],
    ['🌍 Nationality', tenant.nationalite],
    ['🏠 Residence', tenant.country_residence],
    ['🆔 Passport ID', tenant.passport_id],
    ['👨 Adults', tenant.number_adults],
    ['🧒 Children', tenant.number_child],
  ];

  // ================= UI =================
  return (
    <div style={styles.layout}>
      <div style={styles.sidebar}>
        <ManagerSidebar />
      </div>

      <div style={styles.box}>
        <h2 style={styles.heading}>👤 Tenant Details</h2>

        <div style={styles.detailsContainer}>
          {tenantFields.map(([label, value], index) => (
            <div key={index} style={styles.detailBox}>
              <span style={styles.label}>{label}</span>
              <span style={styles.value}>
                <strong>{value || 'N/A'}</strong>
              </span>
            </div>
          ))}

          {/* PASSPORT SECTION */}
          <div style={styles.detailBox}>
            <span style={styles.label}>📝 Passport Document</span>

            {passportUrl ? (
              <>
                <a
                  href={passportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.passportLink}
                >
                  View Passport
                </a>
              </>
            ) : (
              <div style={styles.noDoc}>No document available</div>
            )}
          </div>
        </div>

        {/* PDF PREVIEW */}
        {passportUrl && (
          <iframe
            src={passportUrl}
            title="Passport Preview"
            style={styles.iframe}
          />
        )}

        <button style={styles.closeBtn} onClick={() => navigate(-1)}>
          ⬅ Back
        </button>
      </div>
    </div>
  );
};

export default TenantView;